import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, ExternalLink, HandCoins } from "lucide-react";
import { api, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import {
  PageHeader, TidakPunyaAkses, SearchInput, SortTh, useSortState, sortRows,
  usePaginasi, Paginasi, unduhCsv, TombolEksporCsv, Modal, Field, inputCls, selectCls,
  textareaCls, KOL_PLATFORMS, PLATFORM_LABEL,
} from "./_shared";
import { hariIniISO } from "@/lib/tanggal";
import DealsPanel from "./DealsPanel";

/** ER kosong = "belum bisa dihitung" (followers/average view masih nol atau
 *  belum ada data like/komentar sama sekali) — DITAMPILKAN "—", bukan 0%.
 *  "Belum bisa dihitung" dan "engagement-nya nol" adalah dua kabar yang sangat
 *  berbeda bagi orang yang sedang memilih KOL.
 *  Diekspor supaya `KolDetail.jsx` memakai formatter yang SAMA persis — bukan
 *  disalin (CLAUDE.md: "kalau butuh sesuatu di dua tempat, jadikan satu
 *  sumber"); `_shared.jsx` bukan berkas milik batch ini. */
export function fmtEr(v) {
  if (v === null || v === undefined || v === "") return "—";
  return `${Number(v).toFixed(2)}%`;
}

/** Angka opsional (average view/like/komentar) — kosong tetap "—", tidak
 *  dipaksa jadi 0 seperti `fmtNum(null)`. */
export function fmtAngkaOpsional(v) {
  if (v === null || v === undefined || v === "") return "—";
  return fmtNum(v);
}

const angkaAtauNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const emptyForm = () => ({
  nama: "", platform: KOL_PLATFORMS[0], niche: "", followers: "",
  average_view: "", average_like: "", average_comment: "",
  no_hp: "", rate_card: "", catatan: "",
});

const ER_KOSONG = { er_followers: null, er_views: null };

export default function Kol() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_kol", "view");
  const bolehTambah = can("marketing_kol", "create");
  const bolehEdit = can("marketing_kol", "update");
  const bolehHapus = can("marketing_kol", "delete");

  const [tab, setTab] = useState("direktori");
  // SEMUA saringan di bawah dikirim ke SERVER (lihat `GET /kol` di
  // routers/marketing/kol.py). Menyaring di layar atas daftar yang dipotong
  // server akan melewatkan baris yang tidak terkirim, dan layarnya melapor
  // "tidak ada hasil" dengan yakin — jebakan yang sudah tercatat di CLAUDE.md.
  const [q, setQ] = useState("");
  const qDeb = useDebounce(q, 350);
  const [nicheFilter, setNicheFilter] = useState("");
  const nicheDeb = useDebounce(nicheFilter, 350);
  const [platformFilter, setPlatformFilter] = useState("");
  const [aktifFilter, setAktifFilter] = useState("");
  const [followersMin, setFollowersMin] = useState("");
  const followersMinDeb = useDebounce(followersMin, 350);
  const [followersMax, setFollowersMax] = useState("");
  const followersMaxDeb = useDebounce(followersMax, 350);
  const [erFollowersMin, setErFollowersMin] = useState("");
  const erFollowersMinDeb = useDebounce(erFollowersMin, 350);
  const [erViewsMin, setErViewsMin] = useState("");
  const erViewsMinDeb = useDebounce(erViewsMin, 350);

  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [erPreview, setErPreview] = useState(ER_KOSONG);

  // Saringan HANYA berlaku di tab direktori. Tab "Semua Deal" memakai daftar
  // ini sebagai pilihan KOL saat membuat deal — kalau ikut tersaring, KOL yang
  // sedang tidak cocok filter jadi mustahil dipilih.
  const saringAktif = tab === "direktori";
  const params = useMemo(() => {
    if (!saringAktif) return {};
    const p = {};
    if (qDeb.trim()) p.q = qDeb.trim();
    if (nicheDeb.trim()) p.niche = nicheDeb.trim();
    if (platformFilter) p.platform = platformFilter;
    if (aktifFilter) p.is_active = aktifFilter === "aktif";
    if (followersMinDeb !== "") p.followers_min = Number(followersMinDeb);
    if (followersMaxDeb !== "") p.followers_max = Number(followersMaxDeb);
    if (erFollowersMinDeb !== "") p.er_followers_min = Number(erFollowersMinDeb);
    if (erViewsMinDeb !== "") p.er_views_min = Number(erViewsMinDeb);
    return p;
  }, [saringAktif, qDeb, nicheDeb, platformFilter, aktifFilter, followersMinDeb,
      followersMaxDeb, erFollowersMinDeb, erViewsMinDeb]);

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "kol", params],
    () => api.get("/marketing/kol", { params }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  const rows = useMemo(() => data || [], [data]);
  const sorted = sortRows(rows, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);

  // Pratinjau ER selagi diketik — dihitung DI BACKEND (`POST /kol/er-preview`),
  // bukan di sini. Rumusnya sengaja tidak pernah disalin ke JavaScript: kalau
  // hidup di dua tempat, suatu hari angka di form akan berbeda dari angka di
  // daftar tanpa ada yang sadar (jebakan `total_payments`, CLAUDE.md).
  // Dependency-nya string JSON (primitif, stabil) supaya efek ini tidak ikut
  // jalan setiap huruf nama/catatan berubah.
  const angkaForm = form
    ? JSON.stringify([form.followers, form.average_view, form.average_like, form.average_comment])
    : "";
  const angkaFormDeb = useDebounce(angkaForm, 300);
  useEffect(() => {
    if (!angkaFormDeb) { setErPreview(ER_KOSONG); return undefined; }
    let dibatalkan = false;
    const [followers, averageView, averageLike, averageComment] = JSON.parse(angkaFormDeb);
    api.post("/marketing/kol/er-preview", {
      followers: angkaAtauNull(followers),
      average_view: angkaAtauNull(averageView),
      average_like: angkaAtauNull(averageLike),
      average_comment: angkaAtauNull(averageComment),
    })
      .then((r) => { if (!dibatalkan) setErPreview(r.data || ER_KOSONG); })
      .catch(() => { if (!dibatalkan) setErPreview(ER_KOSONG); });
    return () => { dibatalkan = true; };
  }, [angkaFormDeb]);

  const eksporCsv = () => {
    unduhCsv(`kol-direktori-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "Nama", value: (r) => r.nama },
      { label: "Platform", value: (r) => PLATFORM_LABEL[r.platform] || r.platform },
      { label: "Niche", value: (r) => r.niche ?? r.kategori ?? "" },
      { label: "Followers", value: (r) => r.followers },
      { label: "Average View", value: (r) => r.average_view ?? "" },
      { label: "Average Like", value: (r) => r.average_like ?? "" },
      { label: "Average Comment", value: (r) => r.average_comment ?? "" },
      { label: "ER by Followers (%)", value: (r) => r.er_followers ?? "" },
      { label: "ER by Views (%)", value: (r) => r.er_views ?? "" },
      { label: "No HP", value: (r) => r.no_hp },
      // Angka lama hasil input manual, sudah digantikan dua kolom ER di atas —
      // tetap diekspor supaya data yang terlanjur tersimpan tidak hilang dari
      // pandangan begitu kolomnya lenyap dari tabel.
      { label: "ER Manual (data lama)", value: (r) => r.engagement_rate ?? "" },
      { label: "Status", value: (r) => (r.is_active ? "Aktif" : "Nonaktif") },
    ], sorted);
  };

  const bukaTambah = () => { setEditId(null); setErPreview(ER_KOSONG); setForm(emptyForm()); };
  const bukaEdit = (k) => {
    setEditId(k.id);
    setErPreview({ er_followers: k.er_followers ?? null, er_views: k.er_views ?? null });
    setForm({
      nama: k.nama,
      platform: k.platform,
      // Dokumen lama menyimpannya di `kategori` dan TIDAK dimigrasi.
      niche: k.niche ?? k.kategori ?? "",
      followers: k.followers ?? "",
      average_view: k.average_view ?? "",
      average_like: k.average_like ?? "",
      average_comment: k.average_comment ?? "",
      no_hp: k.no_hp || "",
      rate_card: k.rate_card || "",
      catatan: k.catatan || "",
    });
  };

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama KOL wajib diisi"); return; }
    setBusy(true);
    try {
      const body = {
        nama: form.nama,
        platform: form.platform,
        niche: form.niche,
        followers: form.followers === "" ? 0 : Number(form.followers),
        average_view: angkaAtauNull(form.average_view),
        average_like: angkaAtauNull(form.average_like),
        average_comment: angkaAtauNull(form.average_comment),
        no_hp: form.no_hp,
        rate_card: form.rate_card,
        catatan: form.catatan,
      };
      if (editId) {
        await api.patch(`/marketing/kol/${editId}`, body);
        toast.success("Data KOL diperbarui");
      } else {
        await api.post("/marketing/kol", body);
        toast.success("KOL ditambahkan");
      }
      setForm(null); setEditId(null);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan data KOL"); }
    finally { setBusy(false); }
  };

  const hapus = async (k) => {
    if (!window.confirm(`Hapus KOL "${k.nama}" dari direktori?`)) return;
    try {
      const r = await api.delete(`/marketing/kol/${k.id}`);
      toast.success(r.data.message || "KOL dihapus");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus KOL"); }
  };

  const resetFilter = () => {
    setQ(""); setNicheFilter(""); setPlatformFilter(""); setAktifFilter("");
    setFollowersMin(""); setFollowersMax(""); setErFollowersMin(""); setErViewsMin("");
  };
  const adaFilter = Object.keys(params).length > 0;

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Users} title="KOL & Influencer" />
        <TidakPunyaAkses label="KOL & Influencer" testid="mkt-kol-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-kol">
      <PageHeader
        icon={Users} title="KOL & Influencer"
        desc="Direktori KOL/influencer dan seluruh deal endorsement."
        actions={tab === "direktori" && <>
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-kol-export" />
          {bolehTambah && (
            <button onClick={bukaTambah} data-testid="mkt-kol-tambah"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah KOL
            </button>
          )}
        </>}
      />

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1 w-fit">
        <button onClick={() => setTab("direktori")} data-testid="mkt-kol-tab-direktori"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "direktori" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Users className="w-4 h-4" /> Direktori KOL
        </button>
        <button onClick={() => setTab("deals")} data-testid="mkt-kol-tab-deals"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "deals" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <HandCoins className="w-4 h-4" /> Semua Deal
        </button>
      </div>

      {tab === "direktori" ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput value={q} onChange={setQ} placeholder="Cari nama/kode/niche KOL..." testid="mkt-kol-cari" className="w-56" />
            <input value={nicheFilter} onChange={(e) => setNicheFilter(e.target.value)} data-testid="mkt-kol-filter-niche"
                   placeholder="Niche (cth. Tech)" className={`${inputCls} h-9 w-44`} />
            <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} data-testid="mkt-kol-filter-platform"
                    className={`${selectCls} h-9 w-auto`}>
              <option value="">Semua Platform</option>
              {KOL_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
            </select>
            <select value={aktifFilter} onChange={(e) => setAktifFilter(e.target.value)} data-testid="mkt-kol-filter-aktif"
                    className={`${selectCls} h-9 w-auto`}>
              <option value="">Semua Status</option>
              <option value="aktif">Aktif</option>
              <option value="nonaktif">Nonaktif</option>
            </select>
            <div className="inline-flex items-center gap-1">
              <span className="text-xs text-slate-500">Followers</span>
              <input type="number" min="0" value={followersMin} onChange={(e) => setFollowersMin(e.target.value)}
                     data-testid="mkt-kol-filter-followers-min" placeholder="min" className={`${inputCls} h-9 w-24`} />
              <span className="text-xs text-slate-400">–</span>
              <input type="number" min="0" value={followersMax} onChange={(e) => setFollowersMax(e.target.value)}
                     data-testid="mkt-kol-filter-followers-max" placeholder="maks" className={`${inputCls} h-9 w-24`} />
            </div>
            <div className="inline-flex items-center gap-1">
              <span className="text-xs text-slate-500">ER Followers ≥</span>
              <input type="number" min="0" step="0.1" value={erFollowersMin} onChange={(e) => setErFollowersMin(e.target.value)}
                     data-testid="mkt-kol-filter-er-followers-min" placeholder="%" className={`${inputCls} h-9 w-20`} />
            </div>
            <div className="inline-flex items-center gap-1">
              <span className="text-xs text-slate-500">ER Views ≥</span>
              <input type="number" min="0" step="0.1" value={erViewsMin} onChange={(e) => setErViewsMin(e.target.value)}
                     data-testid="mkt-kol-filter-er-views-min" placeholder="%" className={`${inputCls} h-9 w-20`} />
            </div>
            {adaFilter && (
              <button onClick={resetFilter} data-testid="mkt-kol-filter-reset"
                      className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50">
                Reset filter
              </button>
            )}
          </div>
          {(erFollowersMin !== "" || erViewsMin !== "") && (
            <p className="text-[11px] text-slate-500" data-testid="mkt-kol-catatan-er">
              KOL yang ER-nya belum bisa dihitung (followers/average view masih nol, atau belum ada
              data like &amp; komentar) tidak ikut hasil saringan ER — "belum bisa dihitung" bukan berarti 0%.
            </p>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <SortTh label="Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-kol-sort-nama" />
                    <th className="px-3 py-2.5 font-semibold">Platform</th>
                    <th className="px-3 py-2.5 font-semibold">Niche</th>
                    <SortTh label="Followers" sortKeyName="followers" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-kol-sort-followers" />
                    <SortTh label="Avg View" sortKeyName="average_view" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-kol-sort-avgview" />
                    <SortTh label="Avg Like" sortKeyName="average_like" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-kol-sort-avglike" />
                    <SortTh label="Avg Komentar" sortKeyName="average_comment" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-kol-sort-avgkomentar" />
                    <SortTh label="ER Followers" sortKeyName="er_followers" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-kol-sort-er-followers" />
                    <SortTh label="ER Views" sortKeyName="er_views" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-kol-sort-er-views" />
                    <th className="px-3 py-2.5 font-semibold">Kontak</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                                varian="tabel" kolom={11} pesanKosong="Belum ada KOL yang cocok." data-testid="mkt-kol-state">
                    {halaman.map((k) => (
                      <tr key={k.id} className="hover:bg-slate-50/60" data-testid={`mkt-kol-row-${k.id}`}>
                        <td className="px-3 py-2.5">
                          <p className="font-mono text-[11px] text-slate-400">{k.code}</p>
                          <Link to={`/marketing/kol/${k.id}`} className="font-medium text-slate-900 hover:text-emerald-700 hover:underline">{k.nama}</Link>
                          {!k.is_active && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Nonaktif</span>}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{PLATFORM_LABEL[k.platform] || k.platform}</td>
                        <td className="px-3 py-2.5 text-slate-600" data-testid={`mkt-kol-niche-${k.id}`}>{k.niche || k.kategori || "-"}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{fmtNum(k.followers)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtAngkaOpsional(k.average_view)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtAngkaOpsional(k.average_like)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtAngkaOpsional(k.average_comment)}</td>
                        <td className="px-3 py-2.5 text-right font-mono font-semibold text-slate-800" data-testid={`mkt-kol-er-followers-${k.id}`}>{fmtEr(k.er_followers)}</td>
                        <td className="px-3 py-2.5 text-right font-mono font-semibold text-slate-800" data-testid={`mkt-kol-er-views-${k.id}`}>{fmtEr(k.er_views)}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{k.no_hp || "-"}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {bolehEdit && (
                              <button onClick={() => bukaEdit(k)} data-testid={`mkt-kol-edit-${k.id}`}
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-500 grid place-items-center">
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {bolehHapus && (
                              <button onClick={() => hapus(k)} data-testid={`mkt-kol-hapus-${k.id}`}
                                      className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <Link to={`/marketing/kol/${k.id}`} data-testid={`mkt-kol-detail-${k.id}`}
                                  className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </div>
            <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-kol-paginasi" />
          </div>
        </>
      ) : (
        <DealsPanel kolOptions={rows} />
      )}

      <Modal open={!!form} onClose={() => { setForm(null); setEditId(null); }} title={editId ? "Edit KOL" : "Tambah KOL"} testid="mkt-kol-modal"
             footer={<>
               <button onClick={() => { setForm(null); setEditId(null); }} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-kol-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {busy ? "Menyimpan..." : "Simpan"}
               </button>
             </>}>
        {form && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nama" required>
                <input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))}
                       data-testid="mkt-kol-f-nama" className={inputCls} />
              </Field>
              <Field label="Platform" required>
                <select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}
                        data-testid="mkt-kol-f-platform" className={selectCls}>
                  {KOL_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Niche">
                <input value={form.niche} onChange={(e) => setForm((f) => ({ ...f, niche: e.target.value }))}
                       data-testid="mkt-kol-f-niche" className={inputCls} placeholder="cth. Tech Reviewer" />
              </Field>
              <Field label="Followers">
                <input type="number" min="0" value={form.followers} onChange={(e) => setForm((f) => ({ ...f, followers: e.target.value }))}
                       data-testid="mkt-kol-f-followers" className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Average View">
                <input type="number" min="0" value={form.average_view} onChange={(e) => setForm((f) => ({ ...f, average_view: e.target.value }))}
                       data-testid="mkt-kol-f-average-view" className={inputCls} />
              </Field>
              <Field label="Average Like">
                <input type="number" min="0" value={form.average_like} onChange={(e) => setForm((f) => ({ ...f, average_like: e.target.value }))}
                       data-testid="mkt-kol-f-average-like" className={inputCls} />
              </Field>
              <Field label="Average Comment">
                <input type="number" min="0" value={form.average_comment} onChange={(e) => setForm((f) => ({ ...f, average_comment: e.target.value }))}
                       data-testid="mkt-kol-f-average-comment" className={inputCls} />
              </Field>
            </div>

            {/* ER tidak bisa diketik — ia turunan dari empat angka di atas dan
                dihitung backend. Ditampilkan hidup di sini supaya staf langsung
                melihat akibat angka yang baru diisi, tanpa rumusnya pernah
                disalin ke layar. */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Engagement Rate (by Followers)" hint="Otomatis: (Avg Like + Avg Comment) ÷ Followers">
                <div data-testid="mkt-kol-f-er-followers"
                     className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 text-sm font-mono font-semibold text-slate-800 flex items-center">
                  {fmtEr(erPreview.er_followers)}
                </div>
              </Field>
              <Field label="Engagement Rate (by Views)" hint="Otomatis: (Avg Like + Avg Comment) ÷ Average View">
                <div data-testid="mkt-kol-f-er-views"
                     className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 text-sm font-mono font-semibold text-slate-800 flex items-center">
                  {fmtEr(erPreview.er_views)}
                </div>
              </Field>
            </div>

            <Field label="No HP / WhatsApp">
              <input value={form.no_hp} onChange={(e) => setForm((f) => ({ ...f, no_hp: e.target.value }))}
                     data-testid="mkt-kol-f-hp" className={inputCls} />
            </Field>
            <Field label="Rate Card">
              <input value={form.rate_card} onChange={(e) => setForm((f) => ({ ...f, rate_card: e.target.value }))}
                     data-testid="mkt-kol-f-rate" className={inputCls} placeholder="cth. Post Feed Rp2jt, Story Rp500rb" />
            </Field>
            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))}
                        data-testid="mkt-kol-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>
    </div>
  );
}
